
import React, { useState } from 'react';
import Button from '../base/Button';

interface HeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCity: string;
  onCityChange: (city: string) => void;
  language: string;
  onLanguageChange: (lang: string) => void;
}

export default function Header({
  searchQuery,
  onSearchChange,
  selectedCity,
  onCityChange,
  language,
  onLanguageChange,
}: HeaderProps) {
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const cities = ['Kaikki kaupungit', 'Helsinki', 'Tampere', 'Turku', 'Oulu', 'Jyväskylä', 'Lahti', 'Kuopio', 'Pori', 'Kouvola', 'Kotka'];
  const languages = [
    { code: 'fi', name: 'Suomi' },
    { code: 'sv', name: 'Svenska' },
    { code: 'en', name: 'English' }
  ];

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
              Hankintakalenteri
            </h1>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-2xl mx-8">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </div>
              <input
                type="text"
                placeholder="Hae hankinnoista..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label="Hae hankinnoista"
              />
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-4">
            {/* City selector */}
            <div className="relative">
              <button
                onClick={() => setShowCityDropdown(!showCityDropdown)}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap cursor-pointer"
                aria-expanded={showCityDropdown}
                aria-haspopup="true"
              >
                {selectedCity}
                <i className="ri-arrow-down-s-line ml-1 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </button>
              
              {showCityDropdown && (
                <div className="absolute right-0 z-10 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="py-1" role="menu">
                    {cities.map((city) => (
                      <button
                        key={city}
                        onClick={() => {
                          onCityChange(city);
                          setShowCityDropdown(false);
                        }}
                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left cursor-pointer"
                        role="menuitem"
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Language selector */}
            <div className="relative">
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap cursor-pointer"
                aria-expanded={showLangDropdown}
                aria-haspopup="true"
              >
                {language.toUpperCase()}
                <i className="ri-arrow-down-s-line ml-1 text-sm w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </button>
              
              {showLangDropdown && (
                <div className="absolute right-0 z-10 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="py-1" role="menu">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          onLanguageChange(lang.code);
                          setShowLangDropdown(false);
                        }}
                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left cursor-pointer"
                        role="menuitem"
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Help */}
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg cursor-pointer"
              aria-label="Ohje"
            >
              <i className="ri-question-line text-lg w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {/* Help panel */}
        {showHelp && (
          <div className="absolute right-4 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
            <h3 className="font-semibold text-gray-900 mb-2">Käyttöohje</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p>• Käytä hakukenttää löytääksesi hankinnat nopeasti</p>
              <p>• Suodata tuloksia kategorioiden ja arvokaistan mukaan</p>
              <p>• Tallenna haut ja tilaa ilmoitukset mielenkiintoisista hankinnoista</p>
              <p>• Korttinäkymä tarjoaa yleiskatsauksen, taulukkonäkymä yksityiskohdat</p>
            </div>
            <button
              onClick={() => setShowHelp(false)}
              className="mt-3 text-blue-600 hover:text-blue-800 text-sm cursor-pointer"
            >
              Sulje ohje
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
